import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, FormControl, Select, MenuItem, InputLabel, Alert, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  Pagination, Stack, Divider, InputAdornment
} from '@mui/material';
import {
  Sync as SyncIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  LinkOff as UnmatchedIcon,
  CheckCircle as MatchedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { schoolPayAPI, studentAPI } from '../../services/api';

const SchoolPayTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState({ match_status: '', source: '', search: '' });
  const [syncDate, setSyncDate] = useState(dayjs());
  const [syncRange, setSyncRange] = useState({ from: null, to: null });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [message, setMessage] = useState(null);
  const [matchModal, setMatchModal] = useState(null);
  const [studentSearchOptions, setStudentSearchOptions] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [studentSearchInput, setStudentSearchInput] = useState('');

  const fetchTransactions = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = { ...filters, page: pageNum, per_page: 15 };
      const { data } = await schoolPayAPI.getTransactions(params);
      setTransactions(data.data || []);
      setTotalPages(data.last_page || 1);
      setTotalRecords(data.total || 0);
      setPage(pageNum);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load transactions.' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const { data } = await schoolPayAPI.getTransactionStats();
      setStats(data);
    } catch (err) { /* silent */ }
  };

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [fetchTransactions]);

  const handleSyncDate = async () => {
    if (!syncDate) return;
    setSyncing(true);
    setMessage(null);
    try {
      const { data } = await schoolPayAPI.syncByDate(syncDate.format('YYYY-MM-DD'));
      setMessage({ type: data.success ? 'success' : 'error', text: data.message });
      if (data.success) { fetchTransactions(); fetchStats(); }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncRange = async () => {
    if (!syncRange.from || !syncRange.to) return;
    setSyncing(true);
    setMessage(null);
    try {
      const { data } = await schoolPayAPI.syncByRange(syncRange.from.format('YYYY-MM-DD'), syncRange.to.format('YYYY-MM-DD'));
      setMessage({ type: data.success ? 'success' : 'error', text: data.message });
      if (data.success) { fetchTransactions(); fetchStats(); }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const searchStudents = async (searchTerm) => {
    setStudentSearchInput(searchTerm);
    if (!searchTerm || searchTerm.length < 2) { setStudentSearchOptions([]); return; }
    setStudentSearchLoading(true);
    try {
      const response = await studentAPI.search({ search: searchTerm, per_page: 10 });
      const students = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStudentSearchOptions(students);
    } catch (err) { setStudentSearchOptions([]); }
    finally { setStudentSearchLoading(false); }
  };

  const handleManualMatch = async (student) => {
    if (!matchModal || !student) return;
    try {
      const { data } = await schoolPayAPI.manualMatch({
        transaction_id: matchModal.id,
        student_id: student.id,
      });
      setMessage({ type: 'success', text: data.message || 'Matched successfully.' });
      setMatchModal(null);
      setStudentSearchInput('');
      setStudentSearchOptions([]);
      fetchTransactions(page);
      fetchStats();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Match failed.' });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ match_status: '', source: '', search: '' });
  };

  const getStatusChip = (status) => {
    const config = {
      matched: { label: 'Matched', color: 'success', icon: <MatchedIcon sx={{ fontSize: 14 }} /> },
      pending: { label: 'Pending', color: 'warning', icon: <PendingIcon sx={{ fontSize: 14 }} /> },
      unmatched: { label: 'Unmatched', color: 'error', icon: <UnmatchedIcon sx={{ fontSize: 14 }} /> },
      manual: { label: 'Manual', color: 'info', icon: null },
    };
    const c = config[status] || { label: status, color: 'default' };
    return <Chip label={c.label} color={c.color} size="small" variant="outlined" icon={c.icon} />;
  };

  const hasActiveFilters = filters.match_status || filters.source || filters.search;

  if (loading && !transactions.length) {
    return (
      <Box>
        <Box mb={4}>
          <Skeleton variant="text" width={250} height={32} />
          <Skeleton variant="text" width={300} height={20} />
        </Box>
        <Grid container spacing={2} mb={3}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
        <Card>
          <CardContent>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="text" height={40} sx={{ mb: 1 }} />
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>SchoolPay Transactions</Typography>
        <Typography variant="body1" color="text.secondary">
          View, sync, and manage payments received through SchoolPay
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Revenue summary */}
      {stats && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>SchoolPay collected (posted to fee ledger): UGX {Number(stats.combined_total_amount || 0).toLocaleString()}</strong>
            {' — '}notifications not yet posted: UGX {Number(stats.unmatched_amount || 0).toLocaleString()}
            {' · '}see Fee Management → Payments &amp; Transactions for the combined view
          </Typography>
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total Transactions', value: stats.total, color: 'text.primary' },
            { label: 'Matched', value: stats.matched, color: 'success.main' },
            { label: 'Pending', value: stats.pending, color: 'warning.main' },
            { label: 'Unmatched', value: stats.unmatched, color: 'error.main' },
          ].map(({ label, value, color }) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={label}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
                    {label}
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={color}>
                    {value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Sync Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon fontSize="small" /> Manual Sync
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2} alignItems="center">
              <Grid item size={{ lg: 2.5, md: 2.5, sm: 6, xs: 12 }}>
                <DatePicker
                  label="Sync Date"
                  value={syncDate}
                  onChange={(newValue) => setSyncDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item size={{ lg: 1.5, md: 1.5, sm: 6, xs: 12 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSyncDate}
                  disabled={syncing || !syncDate}
                  startIcon={<SyncIcon />}
                  size="medium"
                >
                  {syncing ? 'Syncing...' : 'Sync'}
                </Button>
              </Grid>
              <Grid item size={{ lg: 0.5, md: 0.5, sm: 0, xs: 0 }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <Divider orientation="vertical" sx={{ height: 30 }} />
              </Grid>
              <Grid item size={{ lg: 2.5, md: 2.5, sm: 6, xs: 12 }}>
                <DatePicker
                  label="From"
                  value={syncRange.from}
                  onChange={(newValue) => setSyncRange(prev => ({ ...prev, from: newValue }))}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item size={{ lg: 2.5, md: 2.5, sm: 6, xs: 12 }}>
                <DatePicker
                  label="To"
                  value={syncRange.to}
                  onChange={(newValue) => setSyncRange(prev => ({ ...prev, to: newValue }))}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item size={{ lg: 2.5, md: 2.5, sm: 12, xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleSyncRange}
                  disabled={syncing || !syncRange.from || !syncRange.to}
                  startIcon={<SyncIcon />}
                  size="medium"
                >
                  Sync Range
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, payment code, receipt #..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item size={{ lg: 3, md: 3, sm: 6, xs: 12 }}>
              <FormControl fullWidth size="small">
                <Select value={filters.match_status} onChange={e => handleFilterChange('match_status', e.target.value)} displayEmpty>
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="matched">Matched</MenuItem>
                  <MenuItem value="unmatched">Unmatched</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item size={{ lg: 2, md: 2, sm: 6, xs: 12 }}>
              <FormControl fullWidth size="small">
                <Select value={filters.source} onChange={e => handleFilterChange('source', e.target.value)} displayEmpty>
                  <MenuItem value="">All Sources</MenuItem>
                  <MenuItem value="sync">Sync</MenuItem>
                  <MenuItem value="webhook">Webhook</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item size={{ lg: 3, md: 3, sm: 12, xs: 12 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                {hasActiveFilters && (
                  <Button size="small" variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                    Clear
                  </Button>
                )}
                <Typography variant="body2" color="text.secondary">
                  Total: <Typography component="span" color="primary" fontWeight="bold">{totalRecords}</Typography>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Transactions</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Receipt #</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No transactions found{hasActiveFilters && ' for the selected filters'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map(txn => (
                    <TableRow key={txn.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {txn.schoolpay_receipt_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{txn.student_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{txn.student_payment_code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          UGX {Number(txn.amount).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{txn.source_payment_channel || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {txn.payment_date ? new Date(txn.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={txn.source} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{getStatusChip(txn.match_status)}</TableCell>
                      <TableCell>
                        {(txn.match_status === 'pending' || txn.match_status === 'unmatched') && (
                          <Button size="small" variant="text" onClick={() => setMatchModal(txn)}>
                            Match
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {(page - 1) * 15 + 1} to {Math.min(page * 15, totalRecords)} of {totalRecords}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => fetchTransactions(newPage)}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Manual Match Dialog */}
      <Dialog open={Boolean(matchModal)} onClose={() => { setMatchModal(null); setStudentSearchInput(''); setStudentSearchOptions([]); }} maxWidth="sm" fullWidth>
        <DialogTitle>Match Transaction to Student</DialogTitle>
        <DialogContent>
          {matchModal && (
            <Box>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Receipt</Typography>
                    <Typography variant="body2" fontWeight={500}>{matchModal.schoolpay_receipt_number}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" fontWeight={500} color="success.main">UGX {Number(matchModal.amount).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Student (SchoolPay)</Typography>
                    <Typography variant="body2">{matchModal.student_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Payment Code</Typography>
                    <Typography variant="body2">{matchModal.student_payment_code || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Autocomplete
                fullWidth
                options={studentSearchOptions}
                inputValue={studentSearchInput}
                onInputChange={(e, val) => searchStudents(val)}
                onChange={(e, student) => { if (student) handleManualMatch(student); }}
                loading={studentSearchLoading}
                getOptionLabel={(option) => option ? `${option.registration_no} - ${option.full_name}` : ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{option.registration_no}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.full_name} ({option.class_stream_name})
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Student to Match"
                    placeholder="Type name or registration number..."
                    helperText="Select a student to credit this payment"
                    margin="normal"
                  />
                )}
                noOptionsText={studentSearchInput.length < 2 ? 'Type at least 2 characters' : 'No students found'}
                filterOptions={(x) => x}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMatchModal(null); setStudentSearchInput(''); setStudentSearchOptions([]); }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolPayTransactions;
